import { Decimal } from 'decimal.js';
import React, { forwardRef } from 'react';
import { NumericFormat, NumericFormatProps } from 'react-number-format';

// Align `defaultValue` with MUI's InputBaseComponentProps (via
// React.HTMLAttributes) so this component is directly assignable to MUI's
// `inputComponent` without casts: accept `readonly string[]` (which
// react-number-format rejects, so it is normalized below) and drop `null`
// (which InputBaseComponentProps rejects).
type NumericFormatMoneyProps = Omit<NumericFormatProps, 'defaultValue'> & {
  defaultValue?: string | number | readonly string[];
  /**
   * Lets the field be empty: an empty input reports `null` instead of 0 and a
   * `null` value renders as an empty input, so the placeholder shows. Without
   * it an empty field is the amount 0.
   */
  shouldAllowEmptyAmount?: boolean;
};

const isEmptyAmount = (value: NumericFormatProps['value']) =>
  value === null || value === undefined || value === '';

export const NumericFormatMoney = forwardRef<
  HTMLInputElement,
  NumericFormatMoneyProps
>(function NumericFormatMoney(props, ref) {
  const {
    onChange,
    value,
    decimalScale,
    defaultValue,
    shouldAllowEmptyAmount = false,
    ...other
  } = props;
  const displayValue =
    shouldAllowEmptyAmount && isEmptyAmount(value)
      ? ''
      : value
        ? new Decimal(value).dividedBy(100).toString()
        : '0';

  return (
    <NumericFormat
      {...other}
      defaultValue={typeof defaultValue === 'object' ? null : defaultValue}
      getInputRef={ref}
      onValueChange={(values) => {
        let newValue: number | Decimal | null;
        if (shouldAllowEmptyAmount && values.value === '') {
          newValue = null;
        } else {
          try {
            newValue = new Decimal(values.value || 0).times(100).toNumber();
          } catch {
            newValue = new Decimal(0);
          }
        }
        if (newValue !== value && onChange) {
          // Synthesized change event whose value is the amount in cents; the
          // handlers MUI/RHF pass down only read `target.name`/`target.value`.
          const event: {
            target: { name?: string; value: string | number | Decimal | null };
          } = {
            target: {
              name: props.name,
              value: newValue,
            },
          };
          onChange(event as React.ChangeEvent<HTMLInputElement>);
        }
      }}
      isAllowed={(values) => {
        const { floatValue } = values;
        return floatValue === undefined || floatValue <= 10000000;
      }}
      minLength={1}
      decimalScale={decimalScale ?? 5}
      thousandSeparator
      valueIsNumericString
      prefix="$"
      value={displayValue}
      onKeyDown={(e) => e.key === '-' && e.preventDefault()}
    />
  );
});
